import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { FightSize } from 'src/app/shared/models/meta-models/fight-size';
import { GearUseRecordResponse } from 'src/app/shared/models/meta-models/gear-use-record-response';
import { GearUseSort } from 'src/app/shared/models/meta-models/gear-use-sort';
import { KillEventPlayerRole } from 'src/app/shared/models/meta-models/kill-event-player-role';
import { SlotType } from 'src/app/shared/models/slot-type.model';
import { JsonReaderService } from '../json-reader/json-reader.service';
import { getSlotTypeByName } from 'src/app/shared/constants/slot-types.constant';
import { KillEventsCounter } from 'src/app/shared/models/meta-items.models';
import { environment } from 'src/environments/environment';
import { AfmLogsService } from '../afm-logs/afm-logs.service';
import { ShopCategory } from 'src/app/shared/models/items.model';

@Injectable({
  providedIn: 'root',
})
export class MetaItemsNewService {
  private readonly apiUrl: string = environment.afmApis.topItems.baseUrl;

  constructor(
    private http: HttpClient,
    private jsonReaderService: JsonReaderService,
    private afmLogsService: AfmLogsService
  ) {
    this.jsonReaderService.ensureLoaded();
  }

  getKillEventsCounters(days: number): Observable<KillEventsCounter[]> {
    return this.http
      .get<KillEventsCounter[]>(`${this.apiUrl}/metaItems/counters`, {
        params: { days },
      })
      .pipe(
        map((counters) =>
          counters.map((counter) => {
            counter.server = counter.server == '0' ? 'west' : counter.server == '1' ? 'east' : counter.server == '2' ? 'europe' : 'unknown';
            counter.dateTime = new Date(counter.dateTime);
            return counter;
          })
        ),
        tap((counters) => {
          counters.sort((a, b) => a.dateTime.getTime() - b.dateTime.getTime());
        })
      );
  }

  getMetaItems(
    server: string,
    days = 28,
    limit = 100,
    slotTypes: SlotType[] | null = null,
    shopSubCategories: ShopCategory[] | null = null,
    killEventPlayerRoles: KillEventPlayerRole[] | null = null,
    fightSizes: FightSize[] | null = null,
    tiers: number[] | null = null,
    enchantments: number[] | null = null,
    sorts: GearUseSort[] | null = null,
    minTotalItemCount: number
  ): Observable<GearUseRecordResponse[]> {
    this.afmLogsService.logSiteUse('User requested meta items');

    const params: any = {
      server,
      days,
      limit,
      minTotalItemCount,
    };

    if (slotTypes) {
      params.slotTypes = slotTypes.map((x) => x.name).join(',');
    }
    if (shopSubCategories) {
      params.shopCatSubCats = shopSubCategories.map((x) => `${x.shopCatSubCat()}`).join(',');
    }
    if (killEventPlayerRoles) {
      params.killEventPlayerRoles = killEventPlayerRoles.map((x) => x.name).join(',');
    }
    if (fightSizes) {
      params.fightSizes = fightSizes.map((x) => x.name).join(',');
    }
    if (tiers) {
      params.tiers = tiers.join(',');
    }
    if (enchantments) {
      params.enchantments = enchantments.join(',');
    }
    if (sorts) {
      params.sorts = sorts.map((x) => x.name).join(',');
    }

    return this.http.get(`${this.apiUrl}/metaItems`, { params }).pipe(
      map((response: any) => {
        return response.map(
          (item: any) =>
            ({
              server: item.server,
              item: this.jsonReaderService.getItemFromIndex(item.uniqueName),
              slotType: getSlotTypeByName(item.slotType),
              totalItemCount: item.totalItemCount,
              totalPlayerCount: item.totalPlayerCount,
              totalEventCount: item.totalEventCount,
              totalKillerCount: item.totalKillerCount,
              totalAssistantCount: item.totalAssistantCount,
              totalVictimCount: item.totalVictimCount,
              killDeathRatio: item.killDeathRatio,
              totalKillFame: item.totalKillFame,
              averageKillFame: item.averageKillFame,
              totalDeathFame: item.totalDeathFame,
              averageDeathFame: item.averageDeathFame,
              totalDamageDone: item.totalDamageDone,
              averageDamageDone: item.averageDamageDone,
              totalSupportHealingDone: item.totalSupportHealingDone,
              averageSupportHealingDone: item.averageSupportHealingDone,
            }) as GearUseRecordResponse
        );
      })
    );
  }
}
